import { 
  collection, 
  doc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { db, storage } from '../firebase';
import type { ContainerVan } from '../types/containerVan';

const COLLECTION_NAME = 'containerVanMasterlist';

/**
 * Get container vans for a specific company
 */
export const getContainerVansByCompany = async (companyId: string): Promise<ContainerVan[]> => {
  try {
    const containerVansRef = collection(db, COLLECTION_NAME);
    const q = query(
      containerVansRef, 
      where('companyId', '==', companyId),
      orderBy('created_at', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const containerVans: ContainerVan[] = [];

    querySnapshot.forEach((doc) => {
      containerVans.push({
        id: doc.id,
        ...doc.data()
      } as ContainerVan);
    });

    return containerVans;
  } catch (error) {
    console.error('Error fetching container vans:', error);
    throw new Error(`Failed to fetch container vans: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Get a single container van by ID
 */
export const getContainerVanById = async (id: string): Promise<ContainerVan | null> => {
  try {
    const containerVanRef = doc(db, COLLECTION_NAME, id);
    const docSnap = await getDoc(containerVanRef);

    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data()
      } as ContainerVan;
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error fetching container van:', error);
    throw new Error(`Failed to fetch container van: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Search container vans by van number
 */
export const searchContainerVansByVanNumber = async (
  vanNumber: string, 
  companyId: string
): Promise<ContainerVan[]> => {
  try {
    const containerVansRef = collection(db, COLLECTION_NAME);
    const q = query(
      containerVansRef,
      where('companyId', '==', companyId),
      orderBy('created_at', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const allContainerVans: ContainerVan[] = [];

    querySnapshot.forEach((doc) => {
      allContainerVans.push({
        id: doc.id,
        ...doc.data()
      } as ContainerVan);
    });

    // Filter by van number (case-insensitive partial match)
    const searchTerm = vanNumber.toLowerCase().trim();
    const filteredContainerVans = allContainerVans.filter(van =>
      van.vanNumber.toLowerCase().includes(searchTerm)
    );

    return filteredContainerVans;
  } catch (error) {
    console.error('Error searching container vans:', error);
    throw new Error(`Failed to search container vans: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Upload container van photo to Firebase Storage
 * Path: /containervan/{companyId}/{checklistId}/{timestamp}.jpg
 */
export const uploadContainerVanPhoto = async (
  companyId: string,
  checklistId: string,
  photoFile: File
): Promise<{ downloadURL: string; storagePath: string }> => {
  try {
    // Create storage path with timestamp
    const timestamp = Math.floor(Date.now() / 1000); // seconds since epoch
    const storagePath = `containervan/${companyId}/${checklistId}/${timestamp}.jpg`;
    const storageRef = ref(storage, storagePath);
    
    // Upload file
    const snapshot = await uploadBytes(storageRef, photoFile);
    
    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    return { downloadURL, storagePath };
  } catch (error) {
    console.error('Error uploading container van photo:', error);
    throw new Error('Failed to upload photo');
  }
};

/**
 * Delete container van photo from Firebase Storage
 */
export const deleteContainerVanPhoto = async (storagePath: string): Promise<void> => {
  try {
    const storageRef = ref(storage, storagePath);
    await deleteObject(storageRef);
  } catch (error) {
    console.error('Error deleting container van photo:', error);
    // Don't throw error if file doesn't exist
    if (error instanceof Error && !error.message.includes('object-not-found')) {
      throw error;
    }
  }
};

/**
 * Validate container van form data
 */
export const validateContainerVanData = (vanNumber: string, genSetNumber: string): Record<string, string> => {
  const errors: Record<string, string> = {};

  if (!vanNumber?.trim()) {
    errors.vanNumber = 'Van number is required';
  }

  if (!genSetNumber?.trim()) {
    errors.genSetNumber = 'Generator set number is required';
  }

  return errors;
};